import React from "react";
import { Component } from "react";
import authService from "./AuthorizeService";
import { AuthenticationResultStatus } from "./AuthorizeService";
import {
  LoginActions,
  QueryParameterNames,
  ApplicationPaths,
} from "./ApiAuthorizationConstants";

// The main responsibility of this component is to handle the user's login process.
// This is the starting point for the login process. Any component that needs to authenticate
// a user can simply perform a redirect to this component with a returnUrl query parameter and
// let the component perform the login and return back to the return url.

// The whole time we are on the page
const GLOBAL_ANIMATION_DURATION = 5000;
// How long the spinner is visible until it is replaced by "Welcome!"
const SPINNER_ANIMATION_DURATION = 3000;
export class Login extends Component {
  constructor(props) {
    super(props);

    this.state = {
      message: undefined,
      hidden: false,
    };
  }

  componentDidMount() {
    setTimeout(() => {
       this.setState({ hidden: true });
    }, SPINNER_ANIMATION_DURATION);

    const action = this.props.action;
    switch (action) {
      case LoginActions.Login:
        this.login(this.getReturnUrl());
        break;
      case LoginActions.LoginCallback:
        this.processLoginCallback();
        break;
      case LoginActions.LoginFailed:
        const params = new URLSearchParams(window.location.search);
        const error = params.get(QueryParameterNames.Message);
        this.setState({ message: error });
        break;
      case LoginActions.Profile:
        this.redirectToProfile();
        break;
      case LoginActions.Register:
        this.redirectToRegister();
        break;
      default:
        throw new Error(`Invalid action '${action}'`);
    }
  }

  render() {
    const action = this.props.action;
    const { message, hidden } = this.state;

    if (!!message) {
      return <div>{message}</div>;
    } else {
      switch (action) {
        case LoginActions.Login:
          return (
            <div className="login-loader-container">
              <h3
                className={`login-welcome ${
                  hidden ? "animate-visible" : "animate-hidden"
                }`}
              >
                Welcome!
              </h3>
              <div
                className={`sk-flow ${
                  hidden ? "animate-hidden" : "animate-visible"
                }`}
              >
                <div class="sk-flow-dot"></div>
                <div class="sk-flow-dot"></div>
                <div class="sk-flow-dot"></div>
              </div>
            </div>
          );
        case LoginActions.LoginCallback:
          // Processing login callback
          return (
            <div className="login-loader-container">
              <h3
                className={`login-welcome ${
                  hidden ? "animate-visible" : "animate-hidden"
                }`}
              >
                Welcome!
              </h3>
              <div
                className={`sk-flow ${
                  hidden ? "animate-hidden" : "animate-visible"
                }`}
              >
                <div className="sk-flow-dot"></div>
                <div className="sk-flow-dot"></div>
                <div className="sk-flow-dot"></div>
              </div>
            </div>
          );
        case LoginActions.Profile:
        case LoginActions.Register:
          return <div></div>;
        default:
          throw new Error(`Invalid action '${action}'`);
      }
    }
  }

  async login(returnUrl) {
    const state = { returnUrl };

    const result = await authService.signIn(state);
    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        break;
      case AuthenticationResultStatus.Success:
        setTimeout(() => {
          this.navigateToReturnUrl(returnUrl);
        }, GLOBAL_ANIMATION_DURATION - 2000);

        break;
      case AuthenticationResultStatus.Fail:
        this.setState({ message: result.message });
        break;
      default:
        throw new Error(`Invalid status result ${result.status}.`);
    }
  }

  async processLoginCallback() {
    const url = window.location.href;
    const result = await authService.completeSignIn(url);
    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        // completeSignIn may return Redirect when OIDC state was lost (e.g. stale
        // tab restored) and it has kicked off a fresh signinRedirect to recover.
        // The browser is already navigating away, so nothing to do here.
        break;
      case AuthenticationResultStatus.Success: {
        const targetReturnUrl = this.getReturnUrl(result.state);
        setTimeout(() => {
          this.navigateToReturnUrl(targetReturnUrl);
        }, GLOBAL_ANIMATION_DURATION - 2000);
        break;
      }
      case AuthenticationResultStatus.Fail:
        this.setState({ message: result.message });
        break;
      default:
        throw new Error(
          `Invalid authentication result status '${result.status}'.`
        );
    }
  }

  getReturnUrl(state) {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get(QueryParameterNames.ReturnUrl);
    if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
      // This is an extra check to prevent open redirects.
      throw new Error(
        "Invalid return url. The return url needs to have the same origin as the current page."
      );
    }

    const fromState = state && typeof state === "object" ? state.returnUrl : null;

    return fromState || fromQuery || `${window.location.origin}/`;
  }

  redirectToRegister() {
    this.redirectToApiAuthorizationPath(
      `${ApplicationPaths.IdentityRegisterPath}?${
        QueryParameterNames.ReturnUrl
      }=${encodeURI(ApplicationPaths.Login)}`
    );
  }

  redirectToProfile() {
    this.redirectToApiAuthorizationPath(ApplicationPaths.IdentityManagePath);
  }

  redirectToApiAuthorizationPath(apiAuthorizationPath) {
    const redirectUrl = `${window.location.origin}/${apiAuthorizationPath}`;
    // It's important that we do a replace here so that when the user hits the back arrow on the
    // browser they get sent back to where it was on the app instead of to an endpoint on this
    // component.
    window.location.replace(redirectUrl);
  }

  navigateToReturnUrl(returnUrl) {
    // Keep callback URL out of history and send user to the original destination.
    try {
      const target = new URL(returnUrl || `${window.location.origin}/`, window.location.origin);

      // Safety check to avoid open redirects.
      if (target.origin !== window.location.origin) {
        window.location.replace(`${window.location.origin}/`);
        return;
      }

      window.location.replace(target.href);
    } catch {
      window.location.replace(`${window.location.origin}/`);
    }
  }
}
